import React, { useMemo } from 'react';
import { useFundData } from '../context/FundDataContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import LoadingSpinner from './LoadingSpinner';
import { formatMonthLabel, formatHoldingsAsOf } from '../utils/format';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Dashboard() {
  const { performance, holdings, ready } = useFundData();

  const { metrics, chartData, error } = useMemo(() => {
    const validData = performance.filter(
      (row) => row.date && row.value !== undefined && row.value !== null
    );

    if (validData.length === 0) {
      return { metrics: null, chartData: null, error: 'No performance data available yet.' };
    }

    const labels = validData.map((row) => formatMonthLabel(row.date));
    const ragnarData = validData.map((row) => Number(row.value));
    const contributedCapitalData = validData.map((row) => Number(row.contributedCapital));

    const latestValue = ragnarData[ragnarData.length - 1];
    const latestCapital = contributedCapitalData[contributedCapitalData.length - 1];
    const totalReturn = latestValue - latestCapital;
    const returnPercentage = latestCapital
      ? ((latestValue / latestCapital - 1) * 100).toFixed(2)
      : '0.00';

    return {
      metrics: { currentValue: latestValue, contributedCapital: latestCapital, totalReturn, returnPercentage },
      chartData: {
        labels,
        datasets: [
          {
            label: 'Portfolio Value',
            data: ragnarData,
            borderColor: '#4f6478',
            backgroundColor: 'rgba(79, 100, 120, 0.12)',
            fill: true,
            tension: 0.35,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 5,
          },
          {
            label: 'Contributed Capital',
            data: contributedCapitalData,
            borderColor: '#98a1ac',
            backgroundColor: 'transparent',
            fill: false,
            tension: 0.35,
            borderWidth: 1.5,
            borderDash: [4, 4],
            pointRadius: 0,
            pointHoverRadius: 5,
          },
        ],
      },
      error: null,
    };
  }, [performance]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, padding: 16, font: { size: 12, family: 'Plus Jakarta Sans, sans-serif' } },
      },
      tooltip: {
        backgroundColor: 'rgba(24, 29, 38, 0.92)',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 45, font: { family: 'Plus Jakarta Sans, sans-serif', size: 11 } } },
      y: {
        grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false },
        ticks: {
          font: { family: 'Plus Jakarta Sans, sans-serif', size: 11 },
          callback: (v) => '$' + v.toLocaleString(),
        },
      },
    },
  };

  return (
    <>
      <div className="client-page-head">
        <h1 className="client-page-title">Dashboard</h1>
        <p className="client-page-sub">Holdings as of {formatHoldingsAsOf(holdings)}</p>
      </div>

      {!ready && <LoadingSpinner text="Loading dashboard..." />}

      {ready && error && <div className="c-card c-card-pad c-empty">{error}</div>}

      {ready && !error && metrics && chartData && (
        <>
          <div className="c-stat-grid">
            <div className="c-stat">
              <span className="c-stat-label">Current value</span>
              <span className="c-stat-value">${metrics.currentValue.toLocaleString()}</span>
            </div>
            <div className="c-stat">
              <span className="c-stat-label">Total return</span>
              <span className="c-stat-value c-pos">${metrics.totalReturn.toLocaleString()}</span>
            </div>
            <div className="c-stat">
              <span className="c-stat-label">Return %</span>
              <span className="c-stat-value c-pos">{metrics.returnPercentage}%</span>
            </div>
            <div className="c-stat">
              <span className="c-stat-label">Contributed capital</span>
              <span className="c-stat-value">${metrics.contributedCapital.toLocaleString()}</span>
            </div>
          </div>

          <div className="c-card">
            <div className="c-card-head">
              <h3>Fund performance since inception</h3>
            </div>
            <div className="c-card-body">
              <div className="c-chart-wrap">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Dashboard;
